import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Card, CardContent } from '../ui/card';
import { CreditCard, Building2, HelpCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FinancialDocumentsModal = ({ open, onClose }) => {
  const [fundingSource, setFundingSource] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!fundingSource) {
      toast.error('Please select a funding source');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/financial/info`, {
        funding_source: fundingSource,
        additional_info: {}
      });
      toast.success('Financial information saved successfully!');
      onClose();
    } catch (error) {
      toast.error('Failed to save financial information');
    } finally {
      setLoading(false);
    }
  };

  const fundingOptions = [
    {
      value: 'personal',
      title: 'Personal Funds',
      description: 'Savings in Account from last 28 days',
      icon: CreditCard
    },
    {
      value: 'banker_loan',
      title: 'Education Loan from our Banker',
      description: 'Get loan assistance from our partner banks',
      icon: Building2
    },
    {
      value: 'need_help',
      title: 'I need help with Education Loan',
      description: 'We assist with Education loan applications',
      icon: HelpCircle
    }
  ];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading font-semibold text-2xl">Financial Documents</DialogTitle>
          <p className="text-muted-foreground">Select your source of funds</p>
        </DialogHeader>

        <div className="space-y-6">
          <RadioGroup value={fundingSource} onValueChange={setFundingSource}>
            <div className="space-y-3">
              {fundingOptions.map((option) => {
                const Icon = option.icon;
                return (
                  <Card
                    key={option.value}
                    className={`cursor-pointer transition-all hover:border-primary/50 ${
                      fundingSource === option.value ? 'border-primary border-2 bg-primary/5' : ''
                    }`}
                    onClick={() => setFundingSource(option.value)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start gap-4">
                        <RadioGroupItem value={option.value} id={option.value} className="mt-1" />
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                              <Icon className="h-5 w-5 text-primary" />
                            </div>
                            <Label htmlFor={option.value} className="font-heading font-semibold text-lg cursor-pointer">
                              {option.title}
                            </Label>
                          </div>
                          <p className="text-sm text-muted-foreground ml-13">{option.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </RadioGroup>

          {fundingSource && (
            <div className="p-4 rounded-xl bg-info/10 border border-info/20">
              <p className="text-sm text-foreground">
                {fundingSource === 'personal' && (
                  <span><strong>Required Documents:</strong> Bank statements for the last 28 days showing sufficient funds</span>
                )}
                {fundingSource === 'banker_loan' && (
                  <span><strong>Next Steps:</strong> Our banking partner will contact you within 24 hours to discuss loan options</span>
                )}
                {fundingSource === 'need_help' && (
                  <span><strong>Next Steps:</strong> Our education loan consultant will reach out to guide you through the process</span>
                )}
              </p>
            </div>
          )}

          <Button
            onClick={handleSubmit}
            disabled={loading || !fundingSource}
            className="w-full rounded-full bg-primary hover:bg-primary/90"
          >
            {loading ? 'Saving...' : 'Continue'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FinancialDocumentsModal;